<script lang="ts">
  import { bootstrap, logout, onAuthChange, type AuthUser } from '../lib/auth';
  import { apiFetch, NotAuthorizedError } from '../lib/api';
  import LoginForm from './LoginForm.svelte';

  let { children }: { children: import('svelte').Snippet<[AuthUser]> } = $props();

  let ready = $state(false);
  let verifying = $state(false);
  let user = $state<AuthUser | null>(null);
  let notAuthorized = $state(false);

  /**
   * Confirm the signed-in user is allowed to use viisi.
   * The shared Supabase project means any Woof user can authenticate,
   * but the backend 403s with "not authorized" unless their sub is in
   * ALLOWED_USER_IDS. Eject them at the gate, not on first real call.
   */
  async function verifyBackendAccess(): Promise<void> {
    verifying = true;
    try {
      await apiFetch('/api/_status');
      notAuthorized = false;
    } catch (err) {
      if (err instanceof NotAuthorizedError) {
        notAuthorized = true;
        logout();
        return;
      }
      // Network errors or backend 500s shouldn't gate access - they'll
      // surface on the first real screen. Log and proceed.
      console.warn('[auth] backend sync ping failed (will surface on first real call):', err);
    } finally {
      verifying = false;
    }
  }

  $effect(() => {
    let cancelled = false;
    let prevUser: AuthUser | null = null;

    const off = onAuthChange((u) => {
      if (cancelled) return;
      const wasNull = !prevUser;
      prevUser = u;
      user = u;
      // Skip during initial boot - the bootstrap branch below handles initial verify.
      if (!ready) return;
      // Post-login transition (LoginForm just succeeded). Verify before letting them in.
      if (wasNull && u) {
        void verifyBackendAccess();
      }
    });

    (async () => {
      try {
        const initial = await bootstrap();
        if (cancelled) return;
        if (initial) await verifyBackendAccess();
      } finally {
        if (!cancelled) ready = true;
      }
    })();

    return () => {
      cancelled = true;
      off();
    };
  });
</script>

{#if !ready || verifying}
  <div class="boot" aria-busy="true" aria-label="Ladataan">
    <div class="dot"></div>
  </div>
{:else if !user}
  <LoginForm {notAuthorized} />
{:else}
  {@render children(user)}
{/if}

<style>
  .boot {
    min-height: 100vh;
    display: flex;
    align-items: center;
    justify-content: center;
    background: var(--viisi-ground);
  }
  .dot {
    width: 12px;
    height: 12px;
    border-radius: 50%;
    background: var(--viisi-accent);
    animation: pulse 1.4s ease-in-out infinite;
  }
  @keyframes pulse {
    0%, 100% { opacity: 1; transform: scale(1); }
    50% { opacity: 0.35; transform: scale(0.85); }
  }
</style>
